import {GetFeaturedWidgetDataResponse, GetWidgetDataResponse} from '@wix/ambassador-events-ui-v1-widget-data/types'
import {addQueryParams, stringifyParams} from '@wix/panda-js-utils'
import {
  EVENT_FILTER_TYPE,
  EVENT_SORT_ORDER,
  EventsHttpClient,
  LIST_LAYOUT,
  RECURRING_FILTER,
  WIDGET_TYPE,
  WidgetData,
} from '@wix/wix-events-commons-statics'
import {getFeaturedWidgetDataServerless, getWidgetDataServerless} from './serverless-widget-data'

export interface CommonWidgetDataParams {
  language: string
  locale: string
  tz: string
  viewMode: string
  members: boolean
  paidPlans: boolean
  responsive: boolean
  newClassicEvents?: boolean
  editor3?: boolean
}

export interface GetWidgetDataParams extends CommonWidgetDataParams {
  compId: string
  showcase: boolean
  fetchCategoryId?: string[]
  fetchAllCategories?: boolean
  widgetType?: WIDGET_TYPE
  listLayout?: LIST_LAYOUT
  recurringFilter?: RECURRING_FILTER
  filterType?: EVENT_FILTER_TYPE
  sortOrder?: EVENT_SORT_ORDER
  categoryId?: string | string[]
  noCategoryIdFallback?: boolean
  limit?: number
  useServerless: boolean
  calendarEvents: boolean
}

export interface GetFeaturedWidgetDataParams extends CommonWidgetDataParams {
  eventId: string
  categoryId: string
  manuallySelected: boolean
  useServerless: boolean
}

export const getWidgetData =
  (api: EventsHttpClient) =>
  async (params: GetWidgetDataParams): Promise<WidgetData | GetWidgetDataResponse> => {
    const {
      responsive,
      locale,
      language,
      compId,
      viewMode,
      members,
      paidPlans,
      widgetType,
      listLayout,
      recurringFilter,
      showcase,
      tz,
      filterType,
      sortOrder,
      categoryId,
      noCategoryIdFallback,
      limit,
      newClassicEvents,
      fetchCategoryId,
      fetchAllCategories,
      editor3,
      useServerless,
      calendarEvents,
    } = params

    if (!useServerless) {
      return api.get<WidgetData>(
        addQueryParams(`/html/v2/widget-data`, {
          ...stringifyParams({
            compId,
            locale: language,
            regional: locale,
            viewMode,
            members,
            paidPlans,
            responsive,
            widgetType,
            listLayout,
            calendarEvents,
            recurringFilter,
            showcase,
            tz,
            filterType,
            sortOrder,
            noCategoryIdFallback,
            limit,
            newClassicEvents,
            editor3,
            fetchBadges: true,
            ...((calendarEvents || listLayout === LIST_LAYOUT.CALENDAR) && {
              today: new Date().toLocaleDateString('en-CA', {timeZone: tz}), // So the cache resets at least everyday
            }),
          }),
          categoryId,
          fetchCategoryId,
          fetchAllCategories,
        }),
      )
    }

    return getWidgetDataServerless(api)(params)
  }

export const getFeaturedWidgetData =
  (api: EventsHttpClient) =>
  async (params: GetFeaturedWidgetDataParams): Promise<WidgetData | GetFeaturedWidgetDataResponse> => {
    const {locale, language, useServerless, ...rest} = params

    if (!useServerless) {
      return api.get(
        addQueryParams(
          '/html/featured-widget-data',
          stringifyParams({
            locale: language,
            regional: locale,
            fetchBadges: true,
            ...rest,
          }),
        ),
      )
    }

    return getFeaturedWidgetDataServerless(api)(params)
  }
